import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../services/distributorService';
import useShippingRequest from '../hooks/useShippingRequest';
import DistributorBankAlertsCredit from './DistributorBankAlertsCredit';
import { useLanguage } from '../i18n/LanguageContext';

const unwrap = (response) => response?.data?.data;

export default function DistributorBankAlerts(props) {
  return props.cash ? (
    <DistributorBankAlertsCredit {...props} />
  ) : (
    <LegacyDistributorBankAlerts {...props} />
  );
}

function LegacyDistributorBankAlerts({ admin = false, readOnly = false, t: providedT }) {
  const { t: languageT, locale } = useLanguage();
  const t = providedT || languageT;
  const { begin, version } = useShippingRequest(`bank-alerts:${admin}:${readOnly}`);
  const busyRef = useRef(false);
  const [preferences, setPreferences] = useState(null);
  const [alerts, setAlerts] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);
  const preferenceMethod = admin ? 'adminBankAlertPreferences' : 'bankAlertPreferences';
  const alertsMethod = admin ? 'adminBankAlerts' : 'bankAlerts';
  useEffect(() => {
    const request = begin();
    busyRef.current = false;
    setBusy(false);
    setPreferences(null);
    setAlerts(null);
    setError('');
    setNotice('');
    Promise.all([distributorService[preferenceMethod](), distributorService[alertsMethod]()])
      .then(([prefResponse, alertsResponse]) => {
        const pref = unwrap(prefResponse),
          rows = unwrap(alertsResponse)?.data;
        if (
          !pref ||
          typeof pref.inApp !== 'boolean' ||
          typeof pref.email !== 'boolean' ||
          pref.threshold !== 100 ||
          !Array.isArray(rows)
        )
          throw new Error('Invalid alerts');
        if (request.isCurrent()) {
          setPreferences(pref);
          setAlerts(rows);
        }
      })
      .catch(() => {
        if (request.isCurrent()) setError('Bank alerts are unavailable. Please try again.');
      });
  }, [begin, version, preferenceMethod, alertsMethod, retry]);
  const save = async (event) => {
    event.preventDefault();
    if (readOnly || busyRef.current || !preferences) return;
    busyRef.current = true;
    const request = begin();
    const payload = { inApp: preferences.inApp, email: preferences.email };
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const method = admin ? 'saveAdminBankAlertPreferences' : 'saveBankAlertPreferences';
      const saved = unwrap(await distributorService[method](payload));
      if (!request.isCurrent()) return;
      if (
        !saved ||
        saved.inApp !== payload.inApp ||
        saved.email !== payload.email ||
        saved.threshold !== 100
      )
        throw new Error('Invalid preference response');
      setPreferences(saved);
      setNotice('Bank alert preferences saved.');
    } catch {
      if (request.isCurrent()) {
        setPreferences(null);
        setAlerts(null);
        setError('Preferences could not be confirmed. Reload and try again.');
      }
    } finally {
      if (request.isCurrent()) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };
  return (
    <section
      aria-label={t('Low-bank alerts')}
      className={`rounded-xl border p-5 space-y-4 ${admin ? 'border-slate-200' : 'me-dw-card me-dw-alerts xl:sticky xl:top-5'}`}
    >
      <div className={`flex items-center justify-between gap-3 ${admin ? '' : 'me-dw-card-head'}`}>
        <h2 className={`font-semibold ${admin ? 'text-slate-900' : 'text-lg text-[#153d48]'}`}>
          {t('Low-bank alerts')}
        </h2>
        <button
          type="button"
          disabled={busy}
          className={admin ? 'text-blue-700 underline' : 'me-dw-link text-sm'}
          onClick={() => {
            if (!busyRef.current) setRetry((value) => value + 1);
          }}
        >
          {t('Refresh')}
        </button>
      </div>
      <p className="text-sm text-slate-600">
        {t(
          readOnly
            ? 'Alerts appear when a product family first falls to 100 units or fewer.'
            : 'Choose how this account is notified when a product family first falls to 100 units or fewer.'
        )}
      </p>
      {error && (
        <p role="alert" className="text-red-700">
          {t(error)}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {t(notice)}
        </p>
      )}
      {!preferences && !error && <p role="status">{t('Loading bank alerts...')}</p>}
      {preferences && (
        <form onSubmit={save} className="space-y-3">
          <label
            className={
              admin
                ? 'flex items-center gap-2'
                : 'me-dw-choice flex items-center gap-2 rounded-lg border border-[#dce9e9] px-3 py-2'
            }
          >
            <input
              type="checkbox"
              checked={preferences.inApp}
              disabled={busy || readOnly}
              onChange={(event) =>
                setPreferences((current) => ({ ...current, inApp: event.target.checked }))
              }
            />
            {t('In-app')}
          </label>
          <label
            className={
              admin
                ? 'flex items-center gap-2'
                : 'me-dw-choice flex items-center gap-2 rounded-lg border border-[#dce9e9] px-3 py-2'
            }
          >
            <input
              type="checkbox"
              checked={preferences.email}
              disabled={busy || readOnly}
              onChange={(event) =>
                setPreferences((current) => ({ ...current, email: event.target.checked }))
              }
            />
            {t('Email')}
          </label>
          {!readOnly && (
            <button
              type="submit"
              disabled={busy}
              className={
                admin
                  ? 'rounded bg-blue-700 px-3 py-2 text-white disabled:opacity-50'
                  : 'me-dw-primary w-full'
              }
            >
              {t('Save alert preferences')}
            </button>
          )}
        </form>
      )}
      {alerts?.length === 0 && (
        <p className="text-sm text-slate-600">{t('No low-bank alerts yet.')}</p>
      )}
      {alerts?.length > 0 && (
        <ul className="me-dw-list border-t border-[#e6eeee]">
          {alerts.map((row) => (
            <li key={row._id} className="py-3 text-sm">
              <strong>{row.familyKey}</strong> ·{' '}
              {t('{units} units remaining', {
                units: Number(row.remainingUnits).toLocaleString(locale),
              })}
              {admin && (
                <span className="text-slate-600"> · {row.distributorName || t('Distributor')}</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
